class ProductForm extends HTMLElement {
  static #META_TAGS_SELECTOR =
    "head :is(title, meta[name], meta[property], link[rel='canonical'], link[rel='alternate'], script[type='application/ld+json'])";

  constructor() {
    super();

    this.section = this.dataset.sectionId
      ? document.getElementById(`shopify-section-${this.dataset.sectionId}`)
      : null;
    this.mediaQueryList = window.matchMedia("(max-width: 64rem)");
    this.variants = this.querySelectorAll("[data-variant]");
    this.chooseSizeLink = this.querySelectorAll("[data-choose-size]");
    this.form = document.getElementById("main-product-form");
    this.bar = this.querySelector("[data-sticky-bar]");

    this.url = new URL(window.location);

    this.observer = this.bar
      ? new IntersectionObserver(
          ([entry]) => {
            this.bar.dataset.visible = entry.isIntersecting ? "false" : "true";
          },
          { threshold: 0 },
        )
      : null;

    this.boundHandleMediaQueryChange = this.handleMediaQueryChange.bind(this);
    this.boundUpdateSizeLink = this.updateSizeLink.bind(this);
    this.boundUpdateProductInfo = this.updateProductInfo.bind(this);
    this.boundAddToCart = this.addToCart.bind(this);
  }

  connectedCallback() {
    this.boundUpdateSizeLink();

    window.addEventListener("resize", this.boundUpdateSizeLink);

    if (this.bar && this.observer) {
      this.bar.dataset.visible = "true";
      this.observer.observe(this);

      if (this.mediaQueryList.matches) {
        document.body.style.marginBlockEnd = `${this.bar.offsetHeight}px`;
      }

      this.mediaQueryList.addEventListener(
        "change",
        this.boundHandleMediaQueryChange,
      );
    }

    this.addEventListener("change", (event) => {
      if (event.target.matches("[data-variant]")) {
        this.updateProductInfo(event);
      }
    });

    this.form.addEventListener("submit", this.boundAddToCart);
  }

  disconnectedCallback() {
    window.removeEventListener("resize", this.boundUpdateSizeLink);

    this.form?.removeEventListener("submit", this.boundAddToCart);
  }

  handleMediaQueryChange(event) {
    if (event.matches) {
      document.body.style.marginBlockEnd = `${this.bar.offsetHeight}px`;
    } else {
      document.body.style.marginBlockEnd = "";
    }
  }

  updateSizeLink() {
    this.chooseSizeLink.forEach((link) => {
      if (window.innerWidth > 1024 && !this.hasSelectedVariant) {
        link.classList.add("disabled");
      } else {
        link.classList.remove("disabled");
      }
    });
  }

  async updateProductInfo(event) {
    document.documentElement.classList.add("cursor-wait");
    document.body.classList.add("pointer-events-none");

    const target = event.target;
    const metaTags = document.querySelectorAll(ProductForm.#META_TAGS_SELECTOR);
    const sectionId = this.dataset.sectionId;
    const sectionToRender = document.querySelector("product-form");

    const url = new URL(
      target.dataset.productUrl ?? this.dataset.productUrl,
      location.origin,
    );

    const response = await fetch(`${url}`);
    const responseText = await response.text();
    const html = new DOMParser().parseFromString(responseText, "text/html");
    const newMetaTags = html.querySelectorAll(ProductForm.#META_TAGS_SELECTOR);
    const newSection = html.getElementById(`shopify-section-${sectionId}`);
    const newSectionToRender = newSection.querySelector("product-form");

    if (!newSection || !newSectionToRender) return;

    this.replacePageContent(
      metaTags,
      newMetaTags,
      sectionToRender,
      newSectionToRender,
      target,
    );
  }

  replacePageContent(
    metaTags,
    newMetaTags,
    sectionToRender,
    newSectionToRender,
    target,
  ) {
    const activeElement = document.activeElement;

    document.startViewTransition(() => {
      sectionToRender.innerHTML = newSectionToRender.innerHTML;
      this.section.removeAttribute("aria-busy");
      this.bar = this.querySelector("[data-sticky-bar]");
      document.documentElement.classList.remove("cursor-wait");
      document.body.classList.remove("pointer-events-none");

      if (this.observer) {
        this.observer.disconnect();
        this.observer.observe(this);
      }

      metaTags.forEach((tag) => tag.remove());
      newMetaTags.forEach((tag) => document.head.appendChild(tag));

      history.replaceState(
        null,
        "",
        `${target.dataset.productUrl ?? location.pathname}`,
      );

      if (!activeElement) return;

      const focusTarget = this.section.querySelector(
        `[data-option-value-id="${activeElement.dataset.optionValueId}"]`,
      );

      if (focusTarget) {
        focusTarget.focus({ preventScroll: true });
      }
    });
  }

  async addToCart(event) {
    event.preventDefault();

    this.section.setAttribute("aria-busy", "true");

    document.documentElement.classList.add("cursor-wait");
    document.body.classList.add("pointer-events-none");

    const formData = new FormData(this.form);

    await fetch("/cart/add.js", { method: "POST", body: formData });

    document.dispatchEvent(new CustomEvent("add-to-cart"));
    document.dispatchEvent(new CustomEvent("cart-updated"));

    this.section.removeAttribute("aria-busy");
    document.documentElement.classList.remove("cursor-wait");
    document.body.classList.remove("pointer-events-none");
  }
}

customElements.define("product-form", ProductForm);
